import { useMemo, useState, type ReactNode } from 'react';
import { TableCell, TableSortLabel } from '@mui/material';

export type SortDir = 'asc' | 'desc';

/**
 * Column sorting for a table rendered from an array already in memory.
 *
 * Every list screen needs it and none of them needed a different answer, so
 * the comparison lives here rather than being written out sixteen times with
 * sixteen opinions about where empty values go.
 *
 * `fields` maps a column key to the value to compare. Returning `null` or
 * `undefined` means "no value", and those rows sort to the bottom in both
 * directions — a row with nothing in the column is not the smallest value, it
 * is an absent one, and burying it under a descending sort hides data.
 */
export interface SortState<K extends string> {
  by: K;
  dir: SortDir;
  /** Toggles direction on the active column, or switches to a new one. */
  toggle: (field: K) => void;
  /** A sortable `<TableCell>` header. */
  head: (field: K, label: ReactNode, align?: 'left' | 'right' | 'center') => ReactNode;
  /** The rows, sorted. Stable: equal rows keep their original order. */
  sorted: <T>(rows: T[], fields: Record<K, (row: T) => unknown>) => T[];
}

export function useSort<K extends string>(
  initialField: K,
  initialDir: SortDir = 'desc',
  onChange?: () => void,
): SortState<K> {
  const [by, setBy] = useState<K>(initialField);
  const [dir, setDir] = useState<SortDir>(initialDir);

  const toggle = (field: K) => {
    if (field === by) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setBy(field);
      // A column you just picked is almost always one you want the top of.
      setDir('desc');
    }
    // Pagination has to go back to the first page, or the sort appears to do
    // nothing while you are looking at page four of the old order.
    onChange?.();
  };

  const head = (field: K, label: ReactNode, align: 'left' | 'right' | 'center' = 'left') => (
    <TableCell align={align} sortDirection={by === field ? dir : false}>
      <TableSortLabel
        active={by === field}
        direction={by === field ? dir : 'desc'}
        onClick={() => toggle(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const sorted = <T,>(rows: T[], fields: Record<K, (row: T) => unknown>): T[] => {
    const pick = fields[by];
    if (!pick) return rows;
    // decorate/sort/undecorate, so the accessor runs once per row and the
    // original index can break ties — Array.sort is not stable across engines
    // for every input size, and a jumping table reads as a bug.
    return rows
      .map((row, i) => ({ row, i, v: pick(row) }))
      .sort((a, b) => directional(a.v, b.v, dir) || a.i - b.i)
      .map((d) => d.row);
  };

  return { by, dir, toggle, head, sorted };
}

/**
 * Comparison with the direction already applied.
 *
 * The emptiness rule is deliberately *outside* the direction: multiplying it
 * by the sign would float the blank rows to the top on a descending sort,
 * which is where they are most in the way.
 */
function directional(a: unknown, b: unknown, dir: SortDir): number {
  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
  return compare(a, b) * (dir === 'asc' ? 1 : -1);
}

function isEmpty(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

/** Rows with no value in the sorted column always sink, whichever way we sort. */
function compare(a: unknown, b: unknown): number {
  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  // Locale-aware and numeric-aware: "item 10" after "item 9", and Turkish
  // letters in their own alphabet's order rather than Unicode's.
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Sorts without the hook, for a list that has no header of its own. */
export function sortBy<T>(rows: T[], pick: (row: T) => unknown, dir: SortDir = 'desc'): T[] {
  return rows
    .map((row, i) => ({ row, i, v: pick(row) }))
    .sort((a, b) => directional(a.v, b.v, dir) || a.i - b.i)
    .map((d) => d.row);
}

/** `compare` on its own, for a screen that sorts in a reducer. */
export { compare as compareValues };

export function useSortMemo<T, K extends string>(
  rows: T[],
  fields: Record<K, (row: T) => unknown>,
  state: SortState<K>,
): T[] {
  return useMemo(() => state.sorted(rows, fields), [rows, state.by, state.dir]);
}
